import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, RefreshCw, Inbox } from "lucide-react";
import { downloadAdminCsv } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Generic admin viewer + CSV exporter. One component, three configurations.
 * Keeps the admin surface area small while delivering identical UX across
 * Contacts / Testimonials / Policy Acknowledgements.
 *
 * Props: { pw, kind } where `kind` is one of: "contacts" | "testimonials" | "policy-acks"
 */
const CONFIGS = {
  "contacts": {
    label: "Contacts",
    listEndpoint:   "/admin/contacts",
    exportEndpoint: "/admin/contacts/export.csv",
    filename:       "usdd-contacts",
    supportsDates:  true,
    columns: [
      { k: "created_at", label: "When", render: (v) => (v || "").slice(0, 16).replace("T", " ") },
      { k: "name",       label: "Name" },
      { k: "email",      label: "Email" },
      { k: "phone",      label: "Phone" },
      { k: "interest",   label: "Interest" },
      { k: "message",    label: "Message", className: "max-w-md truncate" },
    ],
  },
  "testimonials": {
    label: "Testimonials",
    listEndpoint:   "/testimonials",        // public endpoint, returns all
    exportEndpoint: "/admin/testimonials/export.csv",
    filename:       "usdd-testimonials",
    supportsDates:  false,
    publicList:     true,                   // no admin header required for list
    columns: [
      { k: "name",     label: "Name" },
      { k: "role",     label: "Role" },
      { k: "featured", label: "Featured", render: (v) => (v ? "Yes" : "No") },
      { k: "text",     label: "Quote", className: "max-w-lg truncate" },
    ],
  },
  "policy-acks": {
    label: "Policy Acknowledgements",
    listEndpoint:   "/admin/policy-acknowledgements",
    exportEndpoint: "/admin/policy-acknowledgements/export.csv",
    filename:       "usdd-policy-acks",
    supportsDates:  true,
    extraFilters:   [{ key: "policy_slug", label: "Policy slug", placeholder: "e.g. freelance-teacher-payment" }],
    columns: [
      { k: "created_at",  label: "When", render: (v) => (v || "").slice(0, 16).replace("T", " ") },
      { k: "policy_slug", label: "Policy" },
      { k: "full_name",   label: "Name" },
      { k: "email",       label: "Email" },
      { k: "role",        label: "Role" },
    ],
  },
};

export const AdminDataTab = ({ pw, kind }) => {
  const cfg = CONFIGS[kind];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [extra, setExtra] = useState({});

  const params = useMemo(() => {
    const p = {};
    if (cfg.supportsDates) {
      if (dateFrom) p.date_from = dateFrom;
      if (dateTo)   p.date_to   = dateTo;
    }
    Object.entries(extra).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [cfg.supportsDates, dateFrom, dateTo, extra]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = cfg.publicList ? {} : { "x-admin-password": pw };
      const r = await axios.get(`${API}${cfg.listEndpoint}`, { headers, params });
      const data = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't load ${cfg.label.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  }, [pw, cfg, params]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => downloadAdminCsv({
    endpoint: cfg.exportEndpoint,
    params,
    pw,
    filename: cfg.filename,
  });

  return (
    <div data-testid={`admin-${kind}-tab`}>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        {cfg.supportsDates && (
          <>
            <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold flex items-center gap-2">
              From
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid={`${kind}-date-from`}
                className="bg-white/5 border border-white/15 rounded-lg px-2 py-1 text-white text-xs outline-none focus:border-brand-yellow"
              />
            </label>
            <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold flex items-center gap-2">
              To
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid={`${kind}-date-to`}
                className="bg-white/5 border border-white/15 rounded-lg px-2 py-1 text-white text-xs outline-none focus:border-brand-yellow"
              />
            </label>
          </>
        )}
        {(cfg.extraFilters || []).map((f) => (
          <input
            key={f.key}
            placeholder={f.placeholder || f.label}
            value={extra[f.key] || ""}
            onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })}
            data-testid={`${kind}-filter-${f.key}`}
            className="bg-white/5 border border-white/15 rounded-lg px-3 py-1 text-white text-xs outline-none focus:border-brand-yellow"
          />
        ))}
        <button
          onClick={load}
          disabled={loading}
          data-testid={`${kind}-refresh`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-zinc-300 text-[10px] font-bold uppercase tracking-[0.2em] hover:border-brand-yellow hover:text-brand-yellow disabled:opacity-50"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <button
          onClick={exportCsv}
          disabled={loading}
          data-testid={`${kind}-export-csv`}
          className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-brand-yellow text-black text-[10px] font-black uppercase tracking-[0.2em] hover:bg-brand-yellowHover disabled:opacity-50 transition-colors"
        >
          <Download size={11} /> Export filtered CSV
        </button>
      </div>

      {items.length === 0 && !loading ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center">
          <Inbox size={22} className="text-brand-yellow mx-auto mb-3" />
          <div className="text-sm text-zinc-300">No {cfg.label.toLowerCase()} match the current filters.</div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.05] text-[10px] uppercase tracking-[0.22em] text-zinc-400">
              <tr>
                {cfg.columns.map((c) => (
                  <th key={c.k} className="text-left px-3 py-2 font-bold">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={it.id || i} className="border-t border-white/5">
                  {cfg.columns.map((c) => (
                    <td key={c.k} className={`px-3 py-2 text-zinc-200 ${c.className || ""}`}>
                      {c.render ? c.render(it[c.k]) : (it[c.k] || "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-zinc-500 border-t border-white/10">
            {items.length} {cfg.label.toLowerCase()}{items.length === 1 ? "" : ""} shown
          </div>
        </div>
      )}
    </div>
  );
};
